import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { flushQueuedEvents } from '@/lib/tracking';

declare global {
  interface Window { fbq?: (...args: any[]) => void }
}

export const useInitFacebookPixel = () => {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    if (typeof window === 'undefined') return;
    if (window.location.pathname.includes('/admin')) return;

    const loadFbScript = () => {
      if (window.fbq) return; // already loaded
      // Facebook base code
      (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
        if (f.fbq) return; n = f.fbq = function () { n.callMethod ?
          n.callMethod.apply(n, arguments) : n.queue.push(arguments) };
        if (!f._fbq) f._fbq = n; n.push = n; (n as any).loaded = !0; (n as any).version = '2.0';
        (n as any).queue = []; t = b.createElement(e); t.async = !0;
        t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    };

    const init = async () => {
      try {
        // Get active pixel id from backend function (public)
        const { data, error } = await supabase.functions.invoke('get-active-pixel');
        if (error) throw error;
        const pixelId: string | null = data?.pixel_id ?? null;
        if (!pixelId) {
          console.warn('Facebook Pixel: No active pixel configured');
          return;
        }
        loadFbScript();
        // Initialize pixel
        if (window.fbq) {
          window.fbq('init', pixelId);
          // Do NOT auto fire PageView here to avoid duplicates. Route hook handles it.
          initialized.current = true;
          console.log('Facebook Pixel initialized:', pixelId);
          // Flush any queued events collected before fbq was ready
          flushQueuedEvents();
        }
      } catch (e) {
        console.error('Facebook Pixel init error:', e);
      }
    };

    // Defer initialization significantly to improve critical render path
    // Facebook Pixel is not critical for initial page load - delay it
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => init(), { timeout: 5000 });
    } else {
      // Fallback for browsers without requestIdleCallback - defer even more
      setTimeout(() => init(), 3000);
    }
  }, []);
};